import React, { useState, useCallback, useRef } from 'react';
import { Upload, Image, Loader2, CheckCircle, AlertCircle, Sparkles, X, ShieldCheck, ArrowRight, Shield, PartyPopper, Heart, TrendingUp, TrendingDown, Trophy, Flame } from 'lucide-react';
import ProfileCard from '../components/ProfileCard';
import ComparisonCard from '../components/ComparisonCard';
import AlignmentCard from '../components/AlignmentCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Step indicator component
const StepIndicator = ({ currentStep, extractedProfile }) => (
  <div className="flex items-center justify-center gap-4 mb-8">
    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all ${
      currentStep === 'profile' ? 'bg-orange-500/20 border-orange-400 text-orange-400' :
      extractedProfile ? 'bg-green-500/20 border-green-400 text-green-400' : 'border-cyan-500/30 text-cyan-500/50'
    }`}>
      <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-sm font-bold">1</span>
      <span className="font-bold">Perfil</span>
      {extractedProfile && <CheckCircle className="w-4 h-4" />}
    </div>
    
    <ArrowRight className="w-5 h-5 text-cyan-500/50" />
    
    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all ${
      currentStep === 'alignment' ? 'bg-orange-500/20 border-orange-400 text-orange-400' :
      currentStep === 'complete' ? 'bg-green-500/20 border-green-400 text-green-400' : 'border-cyan-500/30 text-cyan-500/50'
    }`}>
      <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-sm font-bold">2</span>
      <span className="font-bold">Alineación</span>
      {currentStep === 'complete' && <CheckCircle className="w-4 h-4" />}
    </div>
  </div>
);

const ExtractPage = () => {
  // Step management: 'profile' | 'alignment' | 'complete'
  const [currentStep, setCurrentStep] = useState('profile');
  
  // Profile state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedProfile, setExtractedProfile] = useState(null);
  const [profileComparison, setProfileComparison] = useState(null);
  const [profileSaveStatus, setProfileSaveStatus] = useState('idle');
  const [profileSaveError, setProfileSaveError] = useState(null);
  const saveInFlightRef = useRef(false);
  
  // Alignment state
  const [alignmentImage, setAlignmentImage] = useState(null);
  const [alignmentPreview, setAlignmentPreview] = useState(null);
  const [isExtractingAlignment, setIsExtractingAlignment] = useState(false);
  const [extractedAlignment, setExtractedAlignment] = useState(null);
  const [alignmentComparison, setAlignmentComparison] = useState(null);
  
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [extractionStep, setExtractionStep] = useState('');
  
  // Warning popup state
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  
  // Site alert popup (BETA message)
  const [showSiteAlert, setShowSiteAlert] = useState(false);
  const [siteAlertData, setSiteAlertData] = useState(null);
  
  // Welcome popup state for new users
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [welcomeData, setWelcomeData] = useState(null);
  
  // Motivation popup state for returning users
  const [showMotivationPopup, setShowMotivationPopup] = useState(false);
  const [motivationData, setMotivationData] = useState(null);
  
  // Name input popup state for unreadable names
  const [showNameInputPopup, setShowNameInputPopup] = useState(false);
  const [nameInputData, setNameInputData] = useState(null);
  const [manualNameInput, setManualNameInput] = useState('');
  
  // Current user info (for linking alignment extractions)
  const [currentUserInfo, setCurrentUserInfo] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Load site alert on mount (only once per session)
  React.useEffect(() => {
    const alertShown = sessionStorage.getItem('siteAlertShown');
    if (!alertShown) {
      axios.get(`${API}/config/site-alert`)
        .then(response => {
          if (response.data.enabled) {
            setSiteAlertData(response.data);
            setShowSiteAlert(true);
          }
        })
        .catch(err => console.log('No site alert configured'));
    }
  }, []);

  const closeSiteAlert = () => {
    setShowSiteAlert(false);
    sessionStorage.setItem('siteAlertShown', 'true');
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [currentStep]);

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida');
      return;
    }
    
    setError(null);
    
    if (currentStep === 'profile') {
      setSelectedImage(file);
      setExtractedProfile(null);
      setProfileComparison(null);
      setProfileSaveStatus('idle');
      setProfileSaveError(null);
      
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      extractProfile(file);
    } else if (currentStep === 'alignment') {
      setAlignmentImage(file);
      setExtractedAlignment(null);
      setAlignmentComparison(null);
      setProfileSaveStatus('idle');
      setProfileSaveError(null);
      
      const reader = new FileReader();
      reader.onload = (e) => setAlignmentPreview(e.target.result);
      reader.readAsDataURL(file);
      extractAlignment(file);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) handleFile(file);
          break;
        }
      }
    }
  }, [currentStep]);

  React.useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // Extract Profile
  const extractProfile = async (imageFile = selectedImage) => {
    if (!imageFile) return;
    
    setIsExtracting(true);
    setError(null);
    setExtractionStep('processing');
    
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await axios.post(`${API}/extract-verify`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Check if name needs manual input (low confidence or null name)
      const gptData = response.data.gpt_data;
      const nameConfidence = gptData?.nameConfidence;
      const detectedName = gptData?.playerName;
      const allianceTag = response.data.userInfo?.allianceTag || gptData?.allianceName;
      
      // If name is null or confidence is low, ask for manual input
      if (!detectedName || nameConfidence === 'low') {
        setNameInputData({
          allianceTag: allianceTag,
          extractedData: response.data,
          detectedName: detectedName // May be partial
        });
        setShowNameInputPopup(true);
        setIsExtracting(false);
        setExtractionStep('');
        return; // Wait for manual input before continuing
      }
      
      // Name was detected with high confidence - continue normally
      processExtractionResult(response.data);
      
    } catch (err) {
      console.error('Error extracting profile:', err);
      setError(err.response?.data?.detail || 'Error al extraer el perfil. Intenta de nuevo.');
    } finally {
      setIsExtracting(false);
      setExtractionStep('');
    }
  };
  
  // Process extraction result (called after name is confirmed)
  const processExtractionResult = (data, manualName = null) => {
    const gptData = data.gpt_data;
    
    // Use manual name if provided, otherwise use detected name
    const finalName = manualName || gptData?.playerName;
    
    // Update gpt_data with the final name
    if (manualName) {
      gptData.playerName = manualName;
    }
    
    // Check if it's a new user and show welcome popup
    const userInfo = data.userInfo;
    if (userInfo) {
      // Save user info for later use with alignment extraction
      setCurrentUserInfo({
        playerId: userInfo.playerId,
        playerName: finalName || userInfo.playerName,
        allianceTag: userInfo.allianceTag
      });
      
      if (userInfo.isNewUser && userInfo.welcomeMessage) {
        setWelcomeData({
          playerName: finalName || userInfo.playerName,
          allianceTag: userInfo.allianceTag,
          welcomeMessage: userInfo.welcomeMessage
        });
        setShowWelcomePopup(true);
      } else if (!userInfo.isNewUser && userInfo.motivationMessage) {
        // Returning user - show motivation message
        setMotivationData({
          playerName: finalName || userInfo.playerName,
          allianceTag: userInfo.allianceTag,
          motivationMessage: userInfo.motivationMessage,
          powerIncreased: userInfo.powerIncreased,
          previousPower: userInfo.previousPower
        });
        setShowMotivationPopup(true);
      }
    }
    
    // Always show extracted profile directly without comparison screen
    const profile = {
      ...gptData,
      playerName: finalName,
      profileImage: data.profileImage
    };
    setExtractedProfile(profile);
    setProfileComparison(null);
  };
  
  // Handle manual name submission
  const handleManualNameSubmit = () => {
    if (!manualNameInput.trim()) return;
    
    const cleanName = manualNameInput.trim();
    setShowNameInputPopup(false);
    
    // Process the extraction with the manual name
    processExtractionResult(nameInputData.extractedData, cleanName);
    
    setManualNameInput('');
    setNameInputData(null);
  };

  const saveCompleteProfile = async (profile, alignment) => {
    if (!profile || !alignment) return;
    if (saveInFlightRef.current) return;

    saveInFlightRef.current = true;
    setProfileSaveStatus('saving');
    setProfileSaveError(null);

    try {
      const payload = {
        ...profile,
        extractedAt: new Date().toISOString(),
        defensiveAlignment: alignment
      };

      const response = await axios.post(`${API}/profiles`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      setProfileSaveStatus('saved');
      localStorage.setItem('lastwar_profiles_updated_at', String(Date.now()));
      window.dispatchEvent(new Event('lastwar-profiles-updated'));

      if (response.data?.userInfo) {
        const userInfo = response.data.userInfo;
        setCurrentUserInfo({
          playerId: userInfo.playerId || userInfo.id,
          playerName: userInfo.playerName,
          allianceTag: userInfo.allianceTag
        });

        if (userInfo.isNewUser && userInfo.welcomeMessage) {
          setWelcomeData({
            playerName: userInfo.playerName,
            allianceTag: userInfo.allianceTag,
            welcomeMessage: userInfo.welcomeMessage
          });
          setShowWelcomePopup(true);
        } else if (!userInfo.isNewUser && userInfo.motivationMessage) {
          setMotivationData({
            playerName: userInfo.playerName,
            allianceTag: userInfo.allianceTag,
            motivationMessage: userInfo.motivationMessage,
            powerIncreased: userInfo.powerIncreased,
            previousPower: userInfo.previousPower
          });
          setShowMotivationPopup(true);
        }
      }
    } catch (err) {
      console.error('Error saving complete profile:', err);
      setProfileSaveStatus('error');
      setProfileSaveError(err.response?.data?.message || err.response?.data?.detail || 'El perfil se extrajo, pero no pude guardarlo en el historial.');
    } finally {
      saveInFlightRef.current = false;
    }
  };

  // Extract Alignment
  const extractAlignment = async (imageFile = alignmentImage) => {
    if (!imageFile) return;
    
    setIsExtractingAlignment(true);
    setError(null);
    setExtractionStep('gpt');
    
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      // Add user info to link this extraction to the user
      if (currentUserInfo) {
        formData.append('playerId', currentUserInfo.playerId);
        formData.append('playerName', currentUserInfo.playerName);
        if (currentUserInfo.allianceTag) {
          formData.append('allianceTag', currentUserInfo.allianceTag);
        }
      }
      
      const response = await axios.post(`${API}/extract-alignment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Direct response without comparison (GPT only)
      const alignment = response.data.data;
      setExtractedAlignment(alignment);
      setAlignmentComparison(null);
      setCurrentStep('complete');
      await saveCompleteProfile(extractedProfile, alignment);
    } catch (err) {
      console.error('Error extracting alignment:', err);
      setError(err.response?.data?.detail || 'Error al extraer la alineación. Intenta de nuevo.');
    } finally {
      setIsExtractingAlignment(false);
      setExtractionStep('');
    }
  };

  const handleProfileConfirm = (resolvedProfile) => {
    setExtractedProfile(resolvedProfile);
    setProfileComparison(null);
  };

  const handleAlignmentConfirm = (resolvedAlignment) => {
    setExtractedAlignment(resolvedAlignment);
    setAlignmentComparison(null);
    setCurrentStep('complete');
    saveCompleteProfile(extractedProfile, resolvedAlignment);
  };

  const proceedToAlignment = () => {
    setCurrentStep('alignment');
  };

  const clearAll = () => {
    setCurrentStep('profile');
    setSelectedImage(null);
    setImagePreview(null);
    setExtractedProfile(null);
    setProfileComparison(null);
    setProfileSaveStatus('idle');
    setProfileSaveError(null);
    saveInFlightRef.current = false;
    setAlignmentImage(null);
    setAlignmentPreview(null);
    setExtractedAlignment(null);
    setAlignmentComparison(null);
    setError(null);
    setProfileSaveStatus('idle');
    setProfileSaveError(null);
  };

  const clearAlignmentImage = () => {
    setAlignmentImage(null);
    setAlignmentPreview(null);
    setExtractedAlignment(null);
    setAlignmentComparison(null);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-5xl font-black text-white mb-3" style={{textShadow: '0 0 20px rgba(255,255,255,0.2)'}}>
          Extraer <span className="text-orange-400">Perfil Completo</span>
        </h1>
        <p className="text-cyan-300/80 text-lg">
          {currentStep === 'profile' && 'Paso 1: Sube la captura de tu perfil'}
          {currentStep === 'alignment' && 'Paso 2: Sube la captura de los escuadrones de tu Muralla'}
          {currentStep === 'complete' && 'Perfil completo listo; se guarda automaticamente'}
        </p>
      </div>

      <StepIndicator currentStep={currentStep} extractedProfile={extractedProfile} />

      {/* STEP 1: Profile Extraction */}
      {currentStep === 'profile' && (
        <>
          {/* Upload Area */}
          <div 
            className={`game-card relative p-2 transition-all duration-300 ${dragActive ? 'border-orange-400 shadow-orange-500/30' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className={`border-2 border-dashed rounded-xl p-8 transition-all ${
              dragActive ? 'border-orange-400 bg-orange-500/10' : 'border-cyan-500/30 hover:border-cyan-400/50'
            }`}>
              {!imagePreview ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-500/30 to-yellow-500/30 rounded-2xl flex items-center justify-center border-2 border-orange-400/50">
                    <Upload className="w-12 h-12 text-orange-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Arrastra la imagen del PERFIL aquí</h3>
                  <p className="text-cyan-300/70 mb-8">o haz clic para seleccionar • También puedes pegar (Ctrl+V)</p>
                  
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" id="file-input" data-testid="file-input" />
                  <button 
                    onClick={() => setShowWarningPopup(true)} 
                    data-testid="select-image-btn" 
                    className="game-btn inline-flex items-center gap-2 cursor-pointer"
                  >
                    <Image className="w-5 h-5" />
                    SELECCIONAR IMAGEN
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <button onClick={clearAll} className="absolute top-3 right-3 z-10 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors border-2 border-red-300 shadow-lg">
                    <X className="w-6 h-6 text-white" />
                  </button>
                  <img src={imagePreview} alt="Preview" className="max-h-96 mx-auto rounded-xl border-3 border-cyan-400/50 shadow-xl" />
                </div>
              )}
            </div>
          </div>

          {/* Loading State */}
          {isExtracting && (
            <div className="game-card p-10 text-center">
              <div className="flex items-center justify-center mb-8">
                <div className="w-24 h-24 mx-auto rounded-2xl flex items-center justify-center border-2 bg-gradient-to-br from-cyan-500 to-blue-600 border-cyan-300 shadow-lg shadow-cyan-500/40">
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Procesando imagen...</h3>
              <p className="text-cyan-300/70">Leyendo los datos del perfil</p>
              <p className="text-yellow-400/80 text-sm mt-3">Esto puede tardar unos segundos.</p>
            </div>
          )}

          {/* Comparison View */}
          {profileComparison && (
            <ComparisonCard comparison={profileComparison} profileImage={profileComparison.profileImage} onConfirm={handleProfileConfirm} />
          )}

          {/* Extracted Profile with Continue Button */}
          {extractedProfile && (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-3 text-green-400">
                <CheckCircle className="w-8 h-8" />
                <span className="font-bold text-xl">¡Perfil verificado correctamente!</span>
              </div>
              
              <ProfileCard profile={extractedProfile} />
              
              <div className="text-center">
                <button onClick={proceedToAlignment} data-testid="continue-alignment-btn"
                  className="game-btn game-btn-green text-xl px-10 py-4 flex items-center gap-3 mx-auto">
                  <CheckCircle className="w-6 h-6" />
                  INFORMACIÓN CORRECTA, CONTINUAR CON MURALLA
                  <ArrowRight className="w-6 h-6" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* STEP 2: Alignment Extraction */}
      {currentStep === 'alignment' && (
        <>
          {/* Show Profile Summary */}
          <div className="game-card p-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-orange-400">
                {extractedProfile?.profileImage ? (
                  <img src={extractedProfile.profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-orange-500/30 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-orange-400" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-cyan-400">Perfil seleccionado:</p>
                <p className="text-xl font-bold text-white">{extractedProfile?.playerName?.replace(/\[.*?\]/g, '').trim() || 'Jugador'}</p>
                <p className="text-sm text-cyan-300/70">Nvl. {extractedProfile?.playerLevel} • {extractedProfile?.power}</p>
              </div>
            </div>
          </div>

          {/* Upload Alignment Image */}
          <div 
            className={`game-card relative p-2 transition-all duration-300 ${dragActive ? 'border-orange-400 shadow-orange-500/30' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className={`border-2 border-dashed rounded-xl p-8 transition-all ${
              dragActive ? 'border-orange-400 bg-orange-500/10' : 'border-cyan-500/30 hover:border-cyan-400/50'
            }`}>
              {!alignmentPreview ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-2xl flex items-center justify-center border-2 border-cyan-400/50">
                    <Shield className="w-12 h-12 text-cyan-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Carga aquí el screenshot de los escuadrones de tu Muralla</h3>
                  <p className="text-cyan-300/70 mb-8">La pantalla que muestra tus 4 escuadrones de la Muralla</p>
                  
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" id="alignment-input" />
                  <label htmlFor="alignment-input">
                    <span className="game-btn game-btn-blue inline-flex items-center gap-2 cursor-pointer">
                      <Image className="w-5 h-5" />
                      SELECCIONAR IMAGEN
                    </span>
                  </label>
                </div>
              ) : (
                <div className="relative">
                  <button onClick={clearAlignmentImage} className="absolute top-3 right-3 z-10 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors border-2 border-red-300 shadow-lg">
                    <X className="w-6 h-6 text-white" />
                  </button>
                  <img src={alignmentPreview} alt="Alignment Preview" className="max-h-96 mx-auto rounded-xl border-3 border-cyan-400/50 shadow-xl" />
                </div>
              )}
            </div>
          </div>

          {/* Loading State - Simple for alignment (GPT only) */}
          {isExtractingAlignment && (
            <div className="game-card p-10 text-center">
              <div className="flex items-center justify-center mb-8">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-3 rounded-2xl flex items-center justify-center border-2 bg-gradient-to-br from-green-500 to-emerald-600 border-green-300 shadow-lg shadow-green-500/50">
                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                  </div>
                  <p className="text-lg font-bold text-green-400">Procesando</p>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Analizando escuadrones y héroes...</h3>
              <p className="text-cyan-300/70">Extrayendo datos de tu alineación defensiva</p>
              <p className="text-yellow-400/80 text-sm mt-3">⏳ Esto puede demorar, por favor aguarda.</p>
            </div>
          )}

        </>
      )}

      {/* STEP 3: Complete - Show Everything */}
      {currentStep === 'complete' && (
        <div className="space-y-8">
          <div className="flex items-center justify-center gap-3 text-green-400">
            <Sparkles className="w-8 h-8" />
            <span className="font-bold text-2xl">¡Perfil Completo Verificado!</span>
            <Sparkles className="w-8 h-8" />
          </div>

          <div className={`game-card p-4 border-2 ${
            profileSaveStatus === 'saved' ? 'border-green-400' :
            profileSaveStatus === 'error' ? 'border-red-500' :
            'border-cyan-500/40'
          }`}>
            <div className="flex items-center justify-center gap-3">
              {profileSaveStatus === 'saving' && <Loader2 className="w-6 h-6 text-cyan-300 animate-spin" />}
              {profileSaveStatus === 'saved' && <CheckCircle className="w-6 h-6 text-green-400" />}
              {profileSaveStatus === 'error' && <AlertCircle className="w-6 h-6 text-red-400" />}
              <span className={`font-bold ${
                profileSaveStatus === 'saved' ? 'text-green-400' :
                profileSaveStatus === 'error' ? 'text-red-400' :
                'text-cyan-300'
              }`}>
                {profileSaveStatus === 'saving' && 'Guardando perfil automaticamente...'}
                {profileSaveStatus === 'saved' && 'Perfil guardado automaticamente en el historial'}
                {profileSaveStatus === 'error' && 'No pude guardar el perfil automaticamente'}
                {profileSaveStatus === 'idle' && 'Preparando guardado automatico...'}
              </span>
            </div>
            {profileSaveError && (
              <p className="text-center text-cyan-100/80 text-sm mt-2">{profileSaveError}</p>
            )}
          </div>

          {/* Profile Card */}
          <div>
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Datos del Perfil</h3>
            <ProfileCard profile={extractedProfile} />
          </div>

          {/* Alignment Card - Editable to select unit types */}
          <div>
            <h3 className="text-xl font-bold text-cyan-400 mb-2">Formación de muralla</h3>
            <p className="text-cyan-300/70 text-sm mb-4">Selecciona el tipo de unidad de cada escuadrón (opcional)</p>
            <AlignmentCard 
              alignment={extractedAlignment} 
              editable={true}
              onUpdateSquadronType={(index, typeId) => {
                // Update the squadron type in extractedAlignment
                const updatedAlignment = { ...extractedAlignment };
                if (updatedAlignment.squadrons && updatedAlignment.squadrons[index]) {
                  updatedAlignment.squadrons[index].unitType = typeId;
                }
                setExtractedAlignment(updatedAlignment);
              }}
            />
          </div>

        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="game-card border-red-500 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center border border-red-500/50">
              <AlertCircle className="w-7 h-7 text-red-400" />
            </div>
            <div>
              <h3 className="text-red-400 font-bold text-lg">Error</h3>
              <p className="text-cyan-100">{error}</p>
            </div>
          </div>
          <p className="text-cyan-100/70 text-sm mb-4">
            😅 Disculpa las molestias. Esto puede pasar ocasionalmente. Por favor, vuelve a intentarlo.
          </p>
          <button 
            onClick={() => {
              setError(null);
              setSelectedImage(null);
              setImagePreview(null);
              setAlignmentImage(null);
              setAlignmentPreview(null);
            }}
            className="game-btn game-btn-blue px-6 py-2 text-sm flex items-center gap-2"
          >
            <ArrowRight className="w-4 h-4" />
            VOLVER A INTENTAR
          </button>
        </div>
      )}

      {/* Tips */}
      {(currentStep === 'profile' || currentStep === 'alignment') && !extractedProfile && !extractedAlignment && (
        <div className="game-card p-6">
          <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Consejos para mejores resultados
          </h3>
          <ul className="space-y-3 text-cyan-100/90">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-sm border border-orange-500/30">1</span>
              <span>Usa capturas de pantalla claras y de buena calidad</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-sm border border-orange-500/30">2</span>
              <span>Asegúrate de que todos los datos sean visibles en la imagen</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-sm border border-orange-500/30">3</span>
              <span>{currentStep === 'profile' ? 'La imagen debe mostrar la pantalla "Mi Perfil" del juego' : 'La imagen debe mostrar la pantalla de los escuadrones de tu Muralla'}</span>
            </li>
          </ul>
        </div>
      )}

      {/* Warning Popup Modal */}
      {showWarningPopup && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-b from-[#1a3a5c] to-[#0d2137] rounded-2xl border-2 border-yellow-500 max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            {/* Warning Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 rounded-full flex items-center justify-center border-4 border-yellow-500/50">
              <AlertCircle className="w-10 h-10 text-yellow-400" />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-black text-yellow-400 text-center mb-4" style={{textShadow: '0 0 20px rgba(250, 204, 21, 0.4)'}}>
              ⚠️ IMPORTANTE
            </h2>
            
            {/* Message */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
              <p className="text-white text-center text-lg leading-relaxed">
                Recuerda que tu información está relacionada con tu usuario. 
                <span className="text-yellow-400 font-bold"> Si lo cambias, contacta al administrador</span> para no perder tu historial.
              </p>
            </div>
            
            {/* Button */}
            <button 
              onClick={() => {
                setShowWarningPopup(false);
                document.getElementById('file-input').click();
              }}
              className="w-full game-btn game-btn-green text-xl py-4 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-6 h-6" />
              ENTENDIDO, CONTINUAR
            </button>
          </div>
        </div>
      )}

      {/* Welcome Popup Modal for NEW USERS */}
      {showWelcomePopup && welcomeData && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-gradient-to-b from-[#1a3a5c] to-[#0d2137] rounded-2xl border-2 border-green-500 max-w-lg w-full p-6 sm:p-8 shadow-2xl animate-in fade-in zoom-in duration-200 my-auto">
            {/* Celebration Icon */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-full flex items-center justify-center border-4 border-green-500/50 animate-bounce">
              <PartyPopper className="w-10 h-10 sm:w-12 sm:h-12 text-green-400" />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-black text-center mb-2" style={{textShadow: '0 0 20px rgba(74, 222, 128, 0.4)'}}>
              <span className="text-green-400">¡BIENVENIDO</span>
            </h2>
            <h3 className="text-xl sm:text-2xl font-black text-yellow-400 text-center mb-4 sm:mb-6" style={{textShadow: '0 0 20px rgba(250, 204, 21, 0.4)'}}>
              {welcomeData.playerName}! 🎉
            </h3>
            
            {/* Alliance Badge */}
            {welcomeData.allianceTag && (
              <div className="flex justify-center mb-4 sm:mb-6">
                <div className="bg-gradient-to-r from-orange-500 to-yellow-500 px-5 sm:px-6 py-2 rounded-full border-2 border-yellow-300 shadow-lg">
                  <span className="text-white font-black text-base sm:text-lg">[{welcomeData.allianceTag}]</span>
                </div>
              </div>
            )}
            
            {/* Message */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 sm:p-5 mb-4 sm:mb-6">
              <p className="text-white text-center text-base sm:text-lg leading-relaxed whitespace-pre-line">
                {welcomeData.welcomeMessage}
              </p>
            </div>
            
            {/* Hearts decoration */}
            <div className="flex justify-center gap-2 mb-4 sm:mb-6">
              <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 animate-pulse" />
              <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-pink-400 animate-pulse" style={{animationDelay: '0.2s'}} />
              <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 animate-pulse" style={{animationDelay: '0.4s'}} />
            </div>
            
            {/* Button */}
            <button 
              onClick={() => setShowWelcomePopup(false)}
              className="w-full game-btn game-btn-green text-lg sm:text-xl py-3 sm:py-4 flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
              ¡GRACIAS! CONTINUAR
            </button>
          </div>
        </div>
      )}

      {/* Motivation Popup Modal for RETURNING USERS */}
      {showMotivationPopup && motivationData && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className={`bg-gradient-to-b ${motivationData.powerIncreased ? 'from-[#1a4a3c] to-[#0d2137] border-emerald-500' : 'from-[#3a2a1a] to-[#1a1510] border-orange-500'} rounded-2xl border-2 max-w-lg w-full p-6 sm:p-8 shadow-2xl animate-in fade-in zoom-in duration-200 my-auto`}>
            {/* Icon based on power change */}
            <div className={`w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 ${motivationData.powerIncreased ? 'bg-gradient-to-br from-emerald-500/30 to-green-500/30 border-emerald-500/50' : 'bg-gradient-to-br from-orange-500/30 to-yellow-500/30 border-orange-500/50'} rounded-full flex items-center justify-center border-4 animate-bounce`}>
              {motivationData.powerIncreased ? (
                <Trophy className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-400" />
              ) : (
                <Flame className="w-10 h-10 sm:w-12 sm:h-12 text-orange-400" />
              )}
            </div>
            
            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-black text-center mb-2" style={{textShadow: motivationData.powerIncreased ? '0 0 20px rgba(52, 211, 153, 0.4)' : '0 0 20px rgba(251, 146, 60, 0.4)'}}>
              <span className={motivationData.powerIncreased ? 'text-emerald-400' : 'text-orange-400'}>
                {motivationData.powerIncreased ? '¡FELICIDADES!' : '¡BIENVENIDO DE VUELTA!'}
              </span>
            </h2>
            <h3 className="text-xl sm:text-2xl font-black text-yellow-400 text-center mb-4 sm:mb-6" style={{textShadow: '0 0 20px rgba(250, 204, 21, 0.4)'}}>
              {motivationData.playerName} {motivationData.powerIncreased ? '🏆' : '💪'}
            </h3>
            
            {/* Power change indicator */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className={`${motivationData.powerIncreased ? 'bg-gradient-to-r from-emerald-600 to-green-500' : 'bg-gradient-to-r from-orange-600 to-yellow-500'} px-5 sm:px-6 py-2 rounded-full border-2 ${motivationData.powerIncreased ? 'border-emerald-300' : 'border-orange-300'} shadow-lg flex items-center gap-2`}>
                {motivationData.powerIncreased ? (
                  <TrendingUp className="w-5 h-5 text-white" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-white" />
                )}
                <span className="text-white font-black text-base sm:text-lg">PODER</span>
              </div>
            </div>
            
            {/* Message */}
            <div className={`${motivationData.powerIncreased ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-orange-500/10 border-orange-500/30'} border rounded-xl p-4 sm:p-5 mb-4 sm:mb-6`}>
              <p className="text-white text-center text-base sm:text-lg leading-relaxed whitespace-pre-line">
                {motivationData.motivationMessage}
              </p>
            </div>
            
            {/* Alliance Badge */}
            {motivationData.allianceTag && (
              <div className="flex justify-center gap-2 mb-4 sm:mb-6">
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-1 rounded-full border-2 border-blue-300 shadow-lg">
                  <span className="text-white font-bold text-sm">[{motivationData.allianceTag}]</span>
                </div>
              </div>
            )}
            
            {/* Button */}
            <button 
              onClick={() => setShowMotivationPopup(false)}
              className={`w-full game-btn ${motivationData.powerIncreased ? 'game-btn-green' : 'game-btn-yellow'} text-lg sm:text-xl py-3 sm:py-4 flex items-center justify-center gap-2`}
            >
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
              {motivationData.powerIncreased ? '¡VAMOS POR MÁS!' : '¡A SEGUIR ADELANTE!'}
            </button>
          </div>
        </div>
      )}

      {/* Name Input Popup for unreadable names */}
      {showNameInputPopup && nameInputData && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-gradient-to-b from-[#1a3a5c] to-[#0d2137] rounded-2xl border-2 border-cyan-500 max-w-lg w-full p-6 sm:p-8 shadow-2xl animate-in fade-in zoom-in duration-200 my-auto">
            {/* Icon */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-full flex items-center justify-center border-4 border-cyan-500/50">
              <PartyPopper className="w-10 h-10 sm:w-12 sm:h-12 text-cyan-400" />
            </div>
            
            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-black text-center mb-2" style={{textShadow: '0 0 20px rgba(34, 211, 238, 0.4)'}}>
              <span className="text-cyan-400">¡Bienvenido miembro de</span>
            </h2>
            
            {/* Alliance Badge */}
            {nameInputData.allianceTag && (
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-r from-orange-500 to-yellow-500 px-5 py-2 rounded-full border-2 border-yellow-300 shadow-lg">
                  <span className="text-white font-black text-lg">{nameInputData.allianceTag}! 🎉</span>
                </div>
              </div>
            )}
            
            {/* Message */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mb-4">
              <p className="text-white text-center text-sm sm:text-base leading-relaxed">
                Tu nombre de usuario tiene caracteres especiales que no podemos leer automáticamente.
              </p>
              <p className="text-cyan-300 text-center text-sm sm:text-base leading-relaxed mt-2">
                Por favor, escríbelo manualmente para poder registrarte correctamente.
              </p>
            </div>
            
            {/* Input */}
            <div className="mb-4">
              <label className="block text-cyan-300 text-sm font-bold mb-2">
                Tu nombre de usuario:
              </label>
              <input
                type="text"
                value={manualNameInput}
                onChange={(e) => setManualNameInput(e.target.value)}
                placeholder="Escribe tu nick aquí..."
                className="w-full px-4 py-3 bg-[#0a1628] border-2 border-cyan-500/50 rounded-xl text-white text-lg focus:border-cyan-400 focus:outline-none transition-colors"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleManualNameSubmit()}
              />
            </div>
            
            {/* Warning */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
              <p className="text-yellow-300 text-xs text-center">
                ⚠️ <strong>Importante:</strong> Escríbelo siempre igual cada vez que uses el sistema, cualquier variación creará un historial diferente.
              </p>
            </div>
            
            {/* Button */}
            <button 
              onClick={handleManualNameSubmit}
              disabled={!manualNameInput.trim()}
              className="w-full game-btn game-btn-green text-lg sm:text-xl py-3 sm:py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
              CONFIRMAR NOMBRE
            </button>
          </div>
        </div>
      )}

      {/* Site Alert Popup (BETA message) */}
      {showSiteAlert && siteAlertData && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4">
          <div className="bg-gradient-to-b from-[#1a3a5c] to-[#0d2137] rounded-2xl border-2 border-yellow-500 max-w-md w-full p-6 sm:p-8 shadow-2xl animate-in fade-in zoom-in duration-200">
            {/* Icon */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 rounded-full flex items-center justify-center border-4 border-yellow-500/50 animate-pulse">
              <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-400" />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-black text-center mb-4 text-yellow-400" style={{textShadow: '0 0 20px rgba(250, 204, 21, 0.4)'}}>
              {siteAlertData.title}
            </h2>
            
            {/* Message */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 sm:p-5 mb-6">
              <p className="text-white text-center text-sm sm:text-base leading-relaxed whitespace-pre-line">
                {siteAlertData.message}
              </p>
            </div>
            
            {/* Button */}
            <button 
              onClick={closeSiteAlert}
              className="w-full game-btn game-btn-yellow text-lg sm:text-xl py-3 sm:py-4 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
              {siteAlertData.buttonText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractPage;
